import React, { useState, useEffect, useCallback } from 'react';

/**
 * A number input that allows clearing the field while typing.
 * Live-commits the parsed number on every keystroke when the value parses
 * cleanly, so consumers get responsive updates (e.g. canvas sizing). Empty or
 * partial input ("", "-", ".") skips the live commit so the field can still
 * be cleared and retyped without snapping back to a fallback. Blur and Enter
 * always commit, applying min/max clamping and falling back when empty.
 */
const NumericInput = ({ value, onChange, min, max, fallback = 0, float = false, className, ...rest }) => {
  const [display, setDisplay] = useState(String(value ?? fallback));
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) {
      setDisplay(String(value ?? fallback));
    }
  }, [value, fallback, isFocused]);

  const clamp = useCallback((parsed) => {
    let v = parsed;
    if (min != null) v = Math.max(min, v);
    if (max != null) v = Math.min(max, v);
    return v;
  }, [min, max]);

  const commit = useCallback((raw) => {
    let parsed = float ? parseFloat(raw) : parseInt(raw, 10);
    if (isNaN(parsed)) parsed = fallback;
    parsed = clamp(parsed);
    setDisplay(String(parsed));
    onChange(parsed);
  }, [onChange, fallback, float, clamp]);

  return (
    <input
      type="number"
      className={className || 'input h-9 tabular-nums'}
      value={display}
      min={min}
      max={max}
      onChange={(e) => {
        const raw = e.target.value;
        setDisplay(raw);
        const parsed = float ? parseFloat(raw) : parseInt(raw, 10);
        if (!isNaN(parsed)) {
          onChange(clamp(parsed));
        }
      }}
      onFocus={() => {
        setIsFocused(true);
        setDisplay(String(value ?? fallback));
      }}
      onBlur={(e) => {
        setIsFocused(false);
        commit(e.target.value);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.target.blur();
        }
      }}
      {...rest}
    />
  );
};

export default NumericInput;
